
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ComponentCanDeactivate } from '../../guards/unsaved-changes.guard';
import { LookupTypeCodes, LookupValue } from '../../models/lookup.models';
import { TimeOffEntry, TimeOffTypeLookup } from '../../models/time-off.models';
import { CapacityReminderEmailPreference } from '../../models/user.models';
import { AuthService } from '../../services/auth.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { LookupService } from '../../services/lookup.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TimeOffService } from '../../services/time-off.service';
import { UserService } from '../../services/user.service';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { CapacityManagementComponent } from '../shared/capacity-management/capacity-management.component';
import { NotificationListComponent } from '../shared/notification-list/notification-list.component';
import { ResourceAttributesComponent } from '../shared/resource-attributes/resource-attributes.component';
import { TimeOffManagementComponent } from '../shared/time-off-management/time-off-management.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    CapacityManagementComponent,
    NotificationListComponent,
    ResourceAttributesComponent,
    TimeOffManagementComponent,
    BreadcrumbComponent,
    MatDivider
  ],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent
  implements OnInit, OnDestroy, ComponentCanDeactivate
{
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private timeOffService = inject(TimeOffService);
  private lookupService = inject(LookupService);
  private featureFlagService = inject(FeatureFlagService);
  private userService = inject(UserService);
  private snackbarService = inject(SnackbarService);

  readonly capacityManagement = viewChild(CapacityManagementComponent);

  readonly timeOffManagement = viewChild(TimeOffManagementComponent);

  userName = '';
  userEmail = '';
  employeeId = '';
  isProfileLoading = true;
  userId: number | null = null;
  emailNotificationsEnabled = false;
  preferredEmailControl = new FormControl<string>('', [Validators.email]);
  isSavingPreferredEmail = false;
  private savedPreferredEmail: string | null = null;
  canEditCapacitySettings = false;
  isTimeOffTrackingEnabled = false;
  isTimeOffApprovalsEnabled = false;
  isNotificationsEnabled = false;
  isEmailNotificationsFeatureEnabled = false;

  // Resource capacity reminder email opt-out. Shown only when the user's primary office has the
  // notification enabled; read-only (forced on) when that office set RequireEmailForAllResources.
  capacityReminderVisible = false;
  capacityReminderLocked = false;
  capacityReminderEmailsEnabled = false;


  // Time off
  timeOffEntries: TimeOffEntry[] = [];
  timeOffTypeLookups: TimeOffTypeLookup[] = [];

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    const isLoggedIn = await this.authService.isLoggedInAsync();
    if (!isLoggedIn) {
      this.router.navigate(['/login']);
      return;
    }

    this.userName = this.authService.getUserName();
    this.userEmail = this.authService.getUserEmail();

    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (fragment === 'notifications') {
        setTimeout(() => {
          document
            .getElementById('notifications-section')
            ?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    });

    // Load feature flags
    this.featureFlagService
      .isFeatureFlagEnabled('time-off-tracking')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isTimeOffTrackingEnabled = enabled;
      });

    this.featureFlagService
      .isFeatureFlagEnabled('time-off-approvals')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isTimeOffApprovalsEnabled = enabled;
      });

    this.featureFlagService
      .isFeatureFlagEnabled('in-app-notifications')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isNotificationsEnabled = enabled;
      });

    this.featureFlagService
      .isFeatureFlagEnabled('email-notifications')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isEmailNotificationsFeatureEnabled = enabled;
      });

    // Subscribe to role changes to update canEditCapacitySettings reactively
    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.canEditCapacitySettings = this.authService.canEditResourceCapacity();
    });

    // Also check immediately in case roles are already loaded
    this.canEditCapacitySettings = this.authService.canEditResourceCapacity();

    // Subscribe to user changes to get employeeId when available
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.isProfileLoading = false;
          this.userId = user.id;
          this.emailNotificationsEnabled = user.emailNotificationsEnabled;
          this.savedPreferredEmail = user.preferredEmail ?? null;
          this.preferredEmailControl.setValue(user.preferredEmail ?? '');
        }
        if (user?.employeeId) {
          this.employeeId = user.employeeId;
          this.loadTimeOffData();
        }
      });

    // Load the capacity-reminder email preference (visibility/lock are derived from the primary
    // office's config server-side; the toggle stays hidden if the office doesn't have it enabled).
    this.userService
      .getMyCapacityReminderEmailPreference()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pref) => this.applyCapacityReminderPreference(pref),
        error: () => {
          // Leave the toggle hidden on failure rather than showing a broken control.
          this.capacityReminderVisible = false;
        },
      });
  }

  private applyCapacityReminderPreference(pref: CapacityReminderEmailPreference): void {
    this.capacityReminderVisible = pref.visible;
    this.capacityReminderLocked = pref.locked;
    this.capacityReminderEmailsEnabled = pref.emailsEnabled;
  }

  canDeactivate(): boolean | Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.checkAllSectionsCanDeactivate()
        .then((canLeave) => {
          observer.next(canLeave);
          observer.complete();
        })
        .catch((error) => {
          // Fail open: a hung guard would trap the user on the page with no
          // feedback, which is worse than an exceptional-case navigation.
          console.error('Error checking unsaved changes on navigation:', error);
          observer.next(true);
          observer.complete();
        });
    });
  }

  /**
   * Checks each editable section in turn (time off, capacity); each
   * prompts for its own unsaved changes. Stops at the first section that blocks.
   */
  private async checkAllSectionsCanDeactivate(): Promise<boolean> {
    const timeOffManagement = this.timeOffManagement();
    if (timeOffManagement?.hasUnsavedChanges()) {
      const canLeave = await timeOffManagement.checkUnsavedChangesForNavigation();
      if (!canLeave) return false;
    }

    const capacityManagement = this.capacityManagement();
    if (capacityManagement) {
      const result = capacityManagement.canDeactivate();
      const canLeave =
        result instanceof Observable ? await firstValueFrom(result) : result;
      if (!canLeave) return false;
    }

    return true;
  }

  // Edit mode coordination between components: only one section may be in edit
  // mode at a time, so entering one first tries to exit the others.
  async onCapacityEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('capacity'))) return;
    this.capacityManagement()?.enterEditMode();
  }

  async onTimeOffEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('timeOff'))) return;
    this.timeOffManagement()?.enterEditMode();
  }

  private async tryExitOtherEditModes(
    except: 'capacity' | 'timeOff',
  ): Promise<boolean> {
    const timeOffManagement = this.timeOffManagement();
    if (except !== 'timeOff' && timeOffManagement?.isEditMode) {
      if (!(await timeOffManagement.tryExitEditMode())) return false;
    }

    const capacityManagement = this.capacityManagement();
    if (except !== 'capacity' && capacityManagement?.isEditMode) {
      if (!(await capacityManagement.tryExitEditMode())) return false;
    }

    return true;
  }

  // Time off save handler (called by TimeOffManagementComponent after successful save)
  onTimeOffSaved(): void {
    this.loadTimeOffData();
  }


  private loadTimeOffData(): void {
    if (!this.employeeId) return;

    // Load lookup types
    this.lookupService
      .getLookupValues(LookupTypeCodes.TIME_OFF_REQUEST_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (values: LookupValue[]) => {
          this.timeOffTypeLookups = values
            .filter((v) => v.isActive)
            .map((v) => ({
              id: v.id,
              code: v.code,
              name: v.name,
              isDefault: v.isDefault,
              sortOrder: v.sortOrder,
            }));
        },
        error: (error) => {
          console.error('Failed to load time off type lookups:', error);
        },
      });

    // Load time off requests from self-service API (no special permission needed)
    this.timeOffService
      .getMyTimeOff()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          this.timeOffEntries = requests.map((r) => ({
            id: r.id,
            displayStartDate: r.displayStartDate
              ? r.displayStartDate.substring(0, 10)
              : null,
            displayEndDate: r.displayEndDate
              ? r.displayEndDate.substring(0, 10)
              : null,
            type: r.typeName,
            typeLookupValueId: r.typeLookupValueId,
            typeCode: r.typeCode,
            statusCode: r.statusCode,
            days: r.days,
            // Without this the calendar never shows the "(Carry-Over)" designation or the
            // event dialog's carry-over row on this page, however the API labels the days.
            carryOverDays: r.carryOverDays,
            daysOff: r.daysOff,
            hasNotes: r.hasNotes,
            hasManagerNotes: r.hasManagerNotes ?? false,
            notesInfo: r.notesInfo ?? null,
          }));
        },
        error: (error) => {
          console.error('Failed to load time off data:', error);
          this.timeOffEntries = [];
        },
      });
  }

  onEmailNotificationsToggle(enabled: boolean): void {
    // Reflect the user's action immediately so the one-way [checked] binding has a distinct value to
    // revert *from* on failure — reassigning the unchanged field wouldn't re-render the toggle.
    this.emailNotificationsEnabled = enabled;
    this.userService
      .updateMyEmailNotifications(enabled)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedUser) => {
          this.emailNotificationsEnabled = updatedUser.emailNotificationsEnabled;
        },
        error: () => {
          // Revert toggle on failure
          this.emailNotificationsEnabled = !enabled;
          this.snackbarService.showError('Failed to update email notification preference. Please try again.');
        },
      });
  }

  onCapacityReminderEmailsToggle(enabled: boolean): void {
    // Reflect the user's action immediately so the one-way [checked] binding has a distinct value to
    // revert *from* on failure — reassigning the unchanged field wouldn't re-render the toggle.
    this.capacityReminderEmailsEnabled = enabled;
    this.userService
      .updateMyCapacityReminderEmails(enabled)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pref) => this.applyCapacityReminderPreference(pref),
        error: () => {
          // Revert toggle on failure
          this.capacityReminderEmailsEnabled = !enabled;
          this.snackbarService.showError('Failed to update capacity reminder email preference. Please try again.');
        },
      });
  }

  get isPreferredEmailUnchanged(): boolean {
    const current = this.preferredEmailControl.value?.trim() || null;
    return current === this.savedPreferredEmail;
  }

  onSetPreferredEmail(): void {    if (this.preferredEmailControl.invalid) return;

    const value = this.preferredEmailControl.value?.trim() || null;
    this.isSavingPreferredEmail = true;

    this.userService
      .updateMyPreferredEmail(value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedUser) => {
          this.savedPreferredEmail = updatedUser.preferredEmail ?? null;
          this.preferredEmailControl.setValue(updatedUser.preferredEmail ?? '');
          this.isSavingPreferredEmail = false;
          this.snackbarService.showSuccess(
            value ? 'Preferred email updated.' : 'Preferred email cleared.'
          );
        },
        error: () => {
          this.isSavingPreferredEmail = false;
          this.snackbarService.showError('Failed to update preferred email. Please try again.');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
